import { useState } from "react";

const NONE: ReadonlySet<string> = new Set();

/**
 * Works out which series should be hidden after a legend click.
 *
 * Ported from the Chart.js `LegendClickHandler` plugin so the behaviour survives
 * the move to recharts:
 *   - everything visible -> isolate the clicked series
 *   - the clicked series is the only visible one -> restore all
 *   - anything else -> plain toggle
 */
export function nextHiddenSeries(
  hidden: ReadonlySet<string>,
  clicked: string,
  allKeys: readonly string[],
): ReadonlySet<string> {
  const visible = allKeys.filter((key) => !hidden.has(key));

  if (visible.length === allKeys.length) {
    return new Set(allKeys.filter((key) => key !== clicked));
  }

  if (visible.length === 1 && visible[0] === clicked) {
    return NONE;
  }

  const next = new Set(hidden);
  if (next.has(clicked)) {
    next.delete(clicked);
  } else {
    next.add(clicked);
  }
  return next;
}

/**
 * Click-to-isolate legend state for a cartesian chart. Spread `legendProps` onto
 * `ChartLegendContent` and `seriesProps(key)` onto each series:
 *
 *   const { legendProps, seriesProps } = useLegendToggle(keys);
 *   <ChartLegend content={<ChartLegendContent {...legendProps} />} />
 *   {keys.map((key) => <Line key={key} dataKey={key} {...seriesProps(key)} />)}
 *
 * Pie and donut charts are out of scope: they hide per `Cell`, not per series.
 */
export function useLegendToggle(allKeys: readonly string[]) {
  const identity = allKeys.join("\n");
  const [state, setState] = useState({ identity, hidden: NONE });

  // Filters change which series are charted. Without this reset an isolated
  // selection survives as a few stray hidden series wherever the key sets overlap.
  const hidden = state.identity === identity ? state.hidden : NONE;

  return {
    legendProps: {
      hiddenSeries: hidden,
      onToggleSeries: (key: string) =>
        setState({ identity, hidden: nextHiddenSeries(hidden, key, allKeys) }),
    },
    seriesProps: (key: string) => ({ hide: hidden.has(key) }),
  };
}
